import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { User, Bell, BellOff, Shield, ShieldCheck, Database, ExternalLink, Mail } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  customer_admin: 'Kundadmin',
  employee: 'Medarbetare',
  read_only_assigner: 'Läsbehörighet (Uppdragsgivare)',
  read_only_participant: 'Läsbehörighet (Deltagare)',
};

const ProfilePage = () => {
  const { user } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dataInfo, setDataInfo] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/users/me');
        setEmailNotifications(res.data.email_notifications ?? true);
      } catch {
        setEmailNotifications(user?.email_notifications ?? true);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [user]);

  const loadDataInfo = async () => {
    if (dataInfo) return;
    try {
      const res = await api.get('/settings/data-info');
      setDataInfo(res.data);
    } catch {
      toast.error('Kunde inte hämta dataskyddsinformation');
    }
  };

  const handleToggle = async (checked) => {
    setEmailNotifications(checked);
    setSaving(true);
    try {
      await api.put('/users/me/notifications', { email_notifications: checked });
      toast.success(checked ? 'E-postnotifieringar aktiverade' : 'E-postnotifieringar avaktiverade');
    } catch {
      setEmailNotifications(!checked);
      toast.error('Kunde inte spara inställning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight">Profil &amp; Inställningar</h1>
          <p className="text-muted-foreground mt-2">Hantera dina kontoinställningar</p>
        </div>

        <Tabs defaultValue="profile" onValueChange={(v) => v === 'dataskydd' && loadDataInfo()}>
          <TabsList className="mb-4">
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="dataskydd">Dataskydd</TabsTrigger>
          </TabsList>

          {/* ── Profil-flik ── */}
          <TabsContent value="profile" className="space-y-6">
            {/* Profile info */}
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                  <User size={20} />
                  Kontoinformation
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wide mb-1">Namn</p>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wide mb-1">E-post</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wide mb-1">Roll</p>
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-muted-foreground" />
                      <p className="font-medium">{ROLE_LABELS[user?.role] || user?.role}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notification settings */}
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                  <Bell size={20} />
                  Notifieringsinställningar
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {!loaded ? (
                  <p className="text-sm text-muted-foreground">Laddar inställningar...</p>
                ) : (
                  <div className="flex items-center justify-between p-4 rounded-lg border-2 border-input">
                    <div className="flex items-center gap-3">
                      {emailNotifications
                        ? <Bell size={20} className="text-primary" />
                        : <BellOff size={20} className="text-muted-foreground" />
                      }
                      <div>
                        <Label htmlFor="email-notif" className="text-base font-semibold cursor-pointer">
                          E-postnotifieringar
                        </Label>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {emailNotifications
                            ? 'Du får e-post vid nya avvikelser och uppföljningspåminnelser'
                            : 'Du får inga e-postmeddelanden från systemet'
                          }
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="email-notif"
                      checked={emailNotifications}
                      onCheckedChange={handleToggle}
                      disabled={saving}
                    />
                  </div>
                )}
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground font-medium mb-2">E-post skickas vid:</p>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Ny avvikelse registreras i din organisation</li>
                    <li>En avvikelse du rapporterat åtgärdas</li>
                    <li>En uppföljningspunkt du ansvarar för förfaller imorgon</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Dataskydd-flik ── */}
          <TabsContent value="dataskydd" className="space-y-6">
            {/* Storage & compliance */}
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                  <ShieldCheck size={20} />
                  Dataskydd &amp; GDPR
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <ShieldCheck size={20} className="text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-green-800">GDPR-kompatibel</p>
                    <p className="text-xs text-green-700">All databehandling sker i enlighet med GDPR (EU) 2016/679</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Database size={16} className="text-muted-foreground" />
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lagringsplats</p>
                    </div>
                    <p className="font-semibold">{dataInfo?.storage_region || 'EU (Frankfurt, eu-central-1)'}</p>
                    <p className="text-xs text-muted-foreground mt-1">{dataInfo?.provider || 'Supabase / AWS'}</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield size={16} className="text-muted-foreground" />
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rättslig grund</p>
                    </div>
                    <p className="text-sm">{dataInfo?.legal_basis || 'Samtycke (Art. 6.1.a) och berättigat intresse (Art. 6.1.f)'}</p>
                  </div>
                </div>

                {dataInfo?.data_retention_policy && (
                  <div className="p-4 bg-muted/40 rounded-lg">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Datalagringspolicy</p>
                    <p className="text-sm">{dataInfo.data_retention_policy}</p>
                  </div>
                )}

                {dataInfo?.data_categories && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Kategorier av personuppgifter</p>
                    <ul className="text-sm space-y-1">
                      {dataInfo.data_categories.map((cat, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          {cat}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact & links */}
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                  <Mail size={20} />
                  Kontakt &amp; Dokument
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="text-sm font-semibold">Dataskyddsombud (DPO)</p>
                    <p className="text-sm text-muted-foreground">{dataInfo?.dpo_contact || 'dataskydd@aiuscore.se'}</p>
                  </div>
                  <a
                    href={`mailto:${dataInfo?.dpo_contact || 'dataskydd@aiuscore.se'}`}
                    className="text-sm font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    Kontakta <ExternalLink size={12} />
                  </a>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="text-sm font-semibold">Dataskyddspolicy</p>
                    <p className="text-xs text-muted-foreground">Fullständig policy för personuppgiftsbehandling</p>
                  </div>
                  <a
                    href={dataInfo?.privacy_policy_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    Öppna <ExternalLink size={12} />
                  </a>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="text-sm font-semibold">Personuppgiftsbiträdesavtal</p>
                    <p className="text-xs text-muted-foreground">DPA för kunder och partners</p>
                  </div>
                  <a
                    href={dataInfo?.processor_agreement_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    Öppna <ExternalLink size={12} />
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
